import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { trackInternalEvent } from '@rio/tracking'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as SimpleForm from 'builder/components/SimpleForm'
import { useI18n } from 'builder/hooks/useI18n'
import { useUser } from 'builder/hooks/useUser'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'
import { actions as userActions } from 'builder/modules/user'
import { DocumentTypes } from 'builder/modules/constants'
import { useDocument } from 'builder/hooks/useDocument'
import { Container, FillWarning, LayoutContainer, Title, TitleWrapper } from '../styles'
import { Routes, TotalPageCount } from '../../constants'
import { ConfirmModal } from '../ConfirmModal/ConfirmModal'
import { usePrompt } from '../../hooks/usePrompt'
import { usePatchForm } from '../../hooks/usePatchForm'
import { useStepsConfig } from '../../hooks/useStepsConfig'
import { FormContainer } from './styles'
import { PersonalInfoForm } from './PersonalInfoForm'

const TRANSLATION = 'builder.auto_apply.form.personal_information'

export type PersonalInfoFormType = Pick<
  AutoApplyForm,
  | 'autoTailoring'
  | 'autoTailoringCl'
  | 'firstName'
  | 'lastName'
  | 'currentJobTitle'
  | 'gender'
  | 'race'
>

export const PersonalInfoFormContainer = () => {
  const user = useUser()
  const dispatch = useDispatch()
  const formState = useSelector(selectors.form)

  const trDocument = useDocument(
    formState.resumeType === DocumentTypes.trDocument ? formState.resumeId : null,
  )

  const form = useForm<PersonalInfoFormType>({
    mode: 'onChange',
  })

  const { mutateAsync: patchForm, isLoading } = usePatchForm()

  const { i18n } = useI18n()
  const navigate = useNavigate()
  const stepsConfig = useStepsConfig()
  const { isPromptVisible, handleCancel, handleConfirm } = usePrompt({
    when: true,
    ignoreParentRoute: ['auto-apply', 'preview', 'tr-documents'],
  })

  const onNextStep = async () => {
    if (
      user?.firstName !== form.getValues('firstName') ||
      user?.lastName !== form.getValues('lastName')
    ) {
      dispatch(
        userActions.updateUserRequest({
          email: user?.email,
          firstName: form.getValues('firstName'),
          lastName: form.getValues('lastName'),
        }),
      )
    }
    await patchForm(form.getValues())
    navigate(`/auto-apply/${stepsConfig[Routes.PERSONAL_INFO].next}`)
  }

  const onBackClick = () => {
    form.clearErrors()
    if (formState.autoTailoring && trDocument) {
      const path = `?path=${Routes.COVER_LETTER}&mode=preview_tailoring`
      navigate(`/documents/${trDocument?.resume_id}/${DocumentTypes.resume}/preview${path}`)
    } else {
      const step = formState.autoTailoring ? Routes.PERSONAL_INFO : Routes.COVER_LETTER
      navigate(`/auto-apply/${stepsConfig[step].prev}`)
    }
  }

  useEffect(() => {
    trackInternalEvent('aa_personal_information', {
      label: 'total_job_search_solution',
    })
  }, [])

  useEffect(() => {
    const { firstName, lastName, currentJobTitle, gender, race } = formState
    form.setValue('firstName', firstName)
    form.setValue('lastName', lastName)
    form.setValue('currentJobTitle', currentJobTitle)
    form.setValue('gender', gender)
    form.setValue('race', race)
  }, [formState])

  return (
    <LayoutContainer>
      <Container>
        <TitleWrapper>
          <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
          <FillWarning>{i18n.t(`${TRANSLATION}.sub_title`)}</FillWarning>
        </TitleWrapper>
        <FormContainer>
          <SimpleForm.Form
            submitButtonText={i18n.t(`${TRANSLATION}.next`)}
            onBackClick={onBackClick}
            onSubmit={form.handleSubmit(onNextStep)}
            isPending={isLoading}
            processIndicator={{
              totalStep: TotalPageCount,
              currentStep: stepsConfig[Routes.PERSONAL_INFO].order,
            }}
          >
            <PersonalInfoForm form={form} />
          </SimpleForm.Form>
        </FormContainer>
      </Container>
      {isPromptVisible && <ConfirmModal onClose={handleCancel} onConfirm={handleConfirm} />}
    </LayoutContainer>
  )
}
